import React from 'react';
import { getData } from '../../requests/CRUD';
import { PROPERTIES } from '../../consts/tables';

const SECTIONS = [
    { code: 'STNL', label: 'STNL Links' },
    { code: 'MT', label: 'MT Links' },
    { code: 'OFFICE', label: 'Office Links' },
];

const STATUS_GROUPS = [
    { code: 'ACTIVE', label: 'Active' },
    { code: 'NEGUC', label: 'In Neg/Under Contract' },
];

const CATEGORY_FILTERS = [
    { value: null, label: 'All Categories' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Office', label: 'Office' },
    { value: 'Industrial', label: 'Industrial' },
    { value: 'Multi-Family', label: 'Multi-Family' },
    { value: 'Other', label: 'Other' },
];

const TYPE_FILTERS = [
    { value: null, label: 'All Types' },
    { value: 'STNL', label: 'Single-Tenant' },
    { value: 'MT', label: 'Multi-Tenant' },
    { value: 'Other', label: 'Other' },
];

function bucketFor(item) {
    if ((item.CATEGORY || '').toLowerCase() === 'office') return 'OFFICE';
    if (item.TYPE === 'STNL') return 'STNL';
    if (item.TYPE === 'MT') return 'MT';
    return null;
}

function statusGroupFor(item) {
    const s = item.STATUS;
    if (s === 'Active') return 'ACTIVE';
    if (s === 'In Negotiation' || s === 'Under Contract') return 'NEGUC';
    return null;
}

function sortByName(a, b) {
    const an = (a.NAME || '').toLowerCase();
    const bn = (b.NAME || '').toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return 0;
}

function buildLabel(item) {
    const name = (item.NAME || '').trim();
    const city = (item.CITY || '').trim();
    const state = (item.STATE || '').trim();
    const location = [city, state].filter(Boolean).join(', ');
    return name && location ? name + ' - ' + location : name || location;
}

function formatPriceShort(val) {
    const n = parseFloat(val);
    if (!n) return '';
    if (n >= 1000000) return '$' + (n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1) + 'M';
    if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
    return '$' + n;
}

function priceBtnLabel(min, max) {
    const hasMin = min !== '';
    const hasMax = max !== '';
    if (hasMin && hasMax) return formatPriceShort(min) + ' – ' + formatPriceShort(max);
    if (hasMin) return '≥ ' + formatPriceShort(min);
    if (hasMax) return '≤ ' + formatPriceShort(max);
    return 'Price Range';
}

function capBtnLabel(min, max) {
    const hasMin = min !== '';
    const hasMax = max !== '';
    if (hasMin && hasMax) return min + '% – ' + max + '%';
    if (hasMin) return '≥ ' + min + '%';
    if (hasMax) return '≤ ' + max + '%';
    return 'Cap Rate';
}

function copyToClipboard(text) {
    if (!text) return Promise.resolve(false);
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => true).catch(() => fallbackCopy(text));
    }
    return Promise.resolve(fallbackCopy(text));
}

function fallbackCopy(text) {
    if (typeof document === 'undefined') return false;
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch (e) {
        return false;
    }
}

const CHEVRON = (
    <svg className='Links_filter-chevron' width='12' height='12' viewBox='0 0 12 12' fill='none'>
        <path d='M2 4l4 4 4-4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
    </svg>
);


export default class Links extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            items: [],
            selectedCategory: null,
            selectedType: null,
            priceMin: '',
            priceMax: '',
            capMin: '',
            capMax: '',
            activeDropdown: null,
        };
        this._filterBarRef = React.createRef();
        this.handleIntroClick = this.handleIntroClick.bind(this);
        this.handleCopy = this.handleCopy.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    componentDidMount() {
        this.fetch();
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    handleClickOutside(e) {
        if (this._filterBarRef.current && !this._filterBarRef.current.contains(e.target)) {
            this.setState({ activeDropdown: null });
        }
    }

    handleKeyDown(e) {
        if (e.key === 'Escape') this.setState({ activeDropdown: null });
    }

    toggleDropdown(name) {
        this.setState(function(s) {
            return { activeDropdown: s.activeDropdown === name ? null : name };
        });
    }

    handleCopy(e) {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) return;

        const rows = document.querySelectorAll('[data-id]');
        const selectedItems = [];
        for (var i = 0; i < rows.length; i++) {
            if (selection.containsNode(rows[i], true)) {
                var id = rows[i].getAttribute('data-id');
                var item = this.state.items.find(function(x) { return String(x.ARCIMEDES_ROW_ID) === id; });
                if (item) selectedItems.push(item);
            }
        }

        if (selectedItems.length === 0) return;

        e.preventDefault();
        var html = selectedItems.map(function(item) {
            var label = buildLabel(item);
            var url = String(item.FLIP_BOOK).trim();
            return '<a href="' + url + '">' + label + '</a>';
        }).join('<br>\n');
        var plain = selectedItems.map(function(item) { return buildLabel(item); }).join('\n');
        e.clipboardData.setData('text/html', html);
        e.clipboardData.setData('text/plain', plain);
    }

    handleIntroClick(e) {
        const text = (e && e.currentTarget && e.currentTarget.textContent) || '';
        copyToClipboard(text.trim());
    }

    fetch() {
        this.setState({ loading: true, error: null });

        const fetchByStatus = (status) => getData({
            tableCode: PROPERTIES,
            options: {
                filter: [
                    { code: 'SOURCE', text: 'SUMMIT', type: 'exact' },
                    { code: 'STATUS', text: status, type: 'exact' },
                    { code: 'FLIP_BOOK', text: '', type: 'notNull' },
                ],
                filterType: 'and',
                column: [
                    'ARCIMEDES_ROW_ID',
                    'NAME',
                    'CITY',
                    'STATE',
                    'TYPE',
                    'CATEGORY',
                    'STATUS',
                    'FLIP_BOOK',
                    'DELETED',
                    'LIST_PRICE',
                    'CAPITALIZATION_RATE',
                ],
                limit: { count: 1000, offset: 0 },
                order: [{ code: 'NAME', type: 'asc' }],
            },
        }).catch(() => ({ success: false, data: [] }));

        Promise.all([
            fetchByStatus('Active'),
            fetchByStatus('In Negotiation'),
            fetchByStatus('Under Contract'),
        ]).then((results) => {
            const anyOk = results.some((r) => r && r.success && Array.isArray(r.data));
            if (!anyOk) {
                this.setState({ items: [], loading: false, error: 'Failed to load listings.' });
                return;
            }
            const items = results
                .filter((r) => r && r.success && Array.isArray(r.data))
                .reduce((acc, r) => acc.concat(r.data), [])
                .filter((row) => !row.DELETED || row.DELETED === '0' || row.DELETED === 0);
            this.setState({ items, loading: false });
        });
    }

    render() {
        const {
            loading, error, items,
            selectedCategory, selectedType,
            priceMin, priceMax, capMin, capMax,
            activeDropdown,
        } = this.state;

        const filteredItems = items.filter((item) => {
            if (selectedCategory && (item.CATEGORY || '').trim().toLowerCase() !== selectedCategory.toLowerCase()) return false;
            if (selectedType && (item.TYPE || '') !== selectedType) return false;
            const price = parseFloat(item.LIST_PRICE);
            if (priceMin !== '' && !isNaN(price) && price < parseFloat(priceMin)) return false;
            if (priceMax !== '' && !isNaN(price) && price > parseFloat(priceMax)) return false;
            const cap = parseFloat(item.CAPITALIZATION_RATE);
            if (capMin !== '' && !isNaN(cap) && cap < parseFloat(capMin)) return false;
            if (capMax !== '' && !isNaN(cap) && cap > parseFloat(capMax)) return false;
            return true;
        });

        const buckets = {
            STNL: { ACTIVE: [], NEGUC: [] },
            MT: { ACTIVE: [], NEGUC: [] },
            OFFICE: { ACTIVE: [], NEGUC: [] },
        };

        filteredItems.forEach((item) => {
            const url = item.FLIP_BOOK && String(item.FLIP_BOOK).trim();
            if (!url) return;
            const b = bucketFor(item);
            const s = statusGroupFor(item);
            if (!b || !s) return;
            buckets[b][s].push(item);
        });

        Object.keys(buckets).forEach((b) => {
            Object.keys(buckets[b]).forEach((s) => {
                buckets[b][s].sort(sortByName);
            });
        });

        const sectionTotal = (code) =>
            buckets[code].ACTIVE.length + buckets[code].NEGUC.length;

        const grandTotal = SECTIONS.reduce((sum, s) => sum + sectionTotal(s.code), 0);

        const ready = !loading && !error;

        const selectedCategoryFilter = CATEGORY_FILTERS.find((c) => c.value === selectedCategory);
        const categoryLabel = selectedCategoryFilter ? selectedCategoryFilter.label : 'Category';
        const selectedTypeFilter = TYPE_FILTERS.find((t) => t.value === selectedType);
        const typeLabel = selectedTypeFilter ? selectedTypeFilter.label : 'Property Type';
        const priceLabel = priceBtnLabel(priceMin, priceMax);
        const capLabel = capBtnLabel(capMin, capMax);
        const priceActive = priceMin !== '' || priceMax !== '';
        const capActive = capMin !== '' || capMax !== '';

        return (
            <div className='Links'>
                <div className='Links_header'>
                    <div className='Links_header-text'>
                        <h1 className='Links_title'>Current Inventory Links</h1>
                        <p className='Links_subtitle'>Flip book links for SUMMIT current listings</p>
                    </div>
                </div>

                <div className='Links_intro-container'>
                    <div className='Links_intro' onClick={this.handleIntroClick} title='Click to copy'>
                        Please click the following links to download the marketing packages:
                    </div>
                    <div className='Links_intro' onClick={this.handleIntroClick} title='Click to copy'>
                        If you have any questions or further interest, please let me know.
                    </div>
                </div>

                <div className='Links_filter-bar' ref={this._filterBarRef}>

                    {/* Category */}
                    <div className='Links_filter-item'>
                        <button
                            className={'Links_filter-btn' + (activeDropdown === 'category' ? ' Links_filter-btn--open' : '') + (selectedCategory ? ' Links_filter-btn--active' : '')}
                            onClick={() => this.toggleDropdown('category')}
                            type='button'
                        >
                            <span>{categoryLabel}</span>
                            {CHEVRON}
                        </button>
                        {activeDropdown === 'category' && (
                            <div className='Links_filter-dropdown'>
                                {CATEGORY_FILTERS.map((cat) => (
                                    <button
                                        key={cat.label}
                                        className={'Links_filter-option' + (selectedCategory === cat.value ? ' Links_filter-option--selected' : '')}
                                        onClick={() => this.setState({ selectedCategory: cat.value, activeDropdown: null })}
                                        type='button'
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Property Type */}
                    <div className='Links_filter-item'>
                        <button
                            className={'Links_filter-btn' + (activeDropdown === 'type' ? ' Links_filter-btn--open' : '') + (selectedType ? ' Links_filter-btn--active' : '')}
                            onClick={() => this.toggleDropdown('type')}
                            type='button'
                        >
                            <span>{typeLabel}</span>
                            {CHEVRON}
                        </button>
                        {activeDropdown === 'type' && (
                            <div className='Links_filter-dropdown'>
                                {TYPE_FILTERS.map((t) => (
                                    <button
                                        key={t.label}
                                        className={'Links_filter-option' + (selectedType === t.value ? ' Links_filter-option--selected' : '')}
                                        onClick={() => this.setState({ selectedType: t.value, activeDropdown: null })}
                                        type='button'
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Price Range */}
                    <div className='Links_filter-item'>
                        <button
                            className={'Links_filter-btn' + (activeDropdown === 'price' ? ' Links_filter-btn--open' : '') + (priceActive ? ' Links_filter-btn--active' : '')}
                            onClick={() => this.toggleDropdown('price')}
                            type='button'
                        >
                            <span>{priceLabel}</span>
                            {CHEVRON}
                        </button>
                        {activeDropdown === 'price' && (
                            <div className='Links_filter-dropdown Links_filter-dropdown--range'>
                                <div className='Links_filter-range-row'>
                                    <div className='Links_filter-range-field'>
                                        <label className='Links_filter-range-label'>Min Price</label>
                                        <div className='Links_filter-range-input-wrap'>
                                            <span className='Links_filter-range-prefix'>$</span>
                                            <input
                                                className='Links_filter-range-input'
                                                type='number'
                                                min='0'
                                                placeholder='0'
                                                value={priceMin}
                                                onChange={(e) => this.setState({ priceMin: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <span className='Links_filter-range-sep'>&ndash;</span>
                                    <div className='Links_filter-range-field'>
                                        <label className='Links_filter-range-label'>Max Price</label>
                                        <div className='Links_filter-range-input-wrap'>
                                            <span className='Links_filter-range-prefix'>$</span>
                                            <input
                                                className='Links_filter-range-input'
                                                type='number'
                                                min='0'
                                                placeholder='No max'
                                                value={priceMax}
                                                onChange={(e) => this.setState({ priceMax: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {priceActive && (
                                    <button
                                        className='Links_filter-clear'
                                        type='button'
                                        onClick={() => this.setState({ priceMin: '', priceMax: '' })}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Cap Rate */}
                    <div className='Links_filter-item'>
                        <button
                            className={'Links_filter-btn' + (activeDropdown === 'cap' ? ' Links_filter-btn--open' : '') + (capActive ? ' Links_filter-btn--active' : '')}
                            onClick={() => this.toggleDropdown('cap')}
                            type='button'
                        >
                            <span>{capLabel}</span>
                            {CHEVRON}
                        </button>
                        {activeDropdown === 'cap' && (
                            <div className='Links_filter-dropdown Links_filter-dropdown--range'>
                                <div className='Links_filter-range-row'>
                                    <div className='Links_filter-range-field'>
                                        <label className='Links_filter-range-label'>Min Cap Rate</label>
                                        <div className='Links_filter-range-input-wrap'>
                                            <input
                                                className='Links_filter-range-input'
                                                type='number'
                                                min='0'
                                                max='100'
                                                step='0.1'
                                                placeholder='0'
                                                value={capMin}
                                                onChange={(e) => this.setState({ capMin: e.target.value })}
                                            />
                                            <span className='Links_filter-range-suffix'>%</span>
                                        </div>
                                    </div>
                                    <span className='Links_filter-range-sep'>&ndash;</span>
                                    <div className='Links_filter-range-field'>
                                        <label className='Links_filter-range-label'>Max Cap Rate</label>
                                        <div className='Links_filter-range-input-wrap'>
                                            <input
                                                className='Links_filter-range-input'
                                                type='number'
                                                min='0'
                                                max='100'
                                                step='0.1'
                                                placeholder='No max'
                                                value={capMax}
                                                onChange={(e) => this.setState({ capMax: e.target.value })}
                                            />
                                            <span className='Links_filter-range-suffix'>%</span>
                                        </div>
                                    </div>
                                </div>
                                {capActive && (
                                    <button
                                        className='Links_filter-clear'
                                        type='button'
                                        onClick={() => this.setState({ capMin: '', capMax: '' })}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                </div>

                {loading && (
                    <div className='Links_loading'>
                        <div className='Links_loading-spinner' />
                        <span>Loading listings...</span>
                    </div>
                )}

                {error && !loading && (
                    <div className='Links_error'>{error}</div>
                )}

                {ready && grandTotal === 0 && (
                    <div className='Links_empty-state'>
                        <p className='Links_empty-state-title'>No flip book links available</p>
                        <p className='Links_empty-state-text'>
                            None of the current SUMMIT listings match the selected filters.
                        </p>
                    </div>
                )}

                {ready && grandTotal > 0 && SECTIONS.map((section) => {
                    const total = sectionTotal(section.code);
                    if (total === 0) return null;
                    return (
                        <section className='Links_section' key={section.code}>
                            <header className='Links_section-header'>
                                <h2 className='Links_section-title'>{section.label}</h2>
                                <span className='Links_section-count'>{total}</span>
                            </header>
                            {STATUS_GROUPS.map((group) => {
                                const list = buckets[section.code][group.code];
                                if (list.length === 0) return null;
                                return (
                                    <div className={'Links_group Links_group--' + group.code.toLowerCase()} key={group.code}>
                                        <div className='Links_group-header'>
                                            <span className='Links_group-dot' />
                                            <h3 className='Links_group-title'>{group.label}</h3>
                                            <span className='Links_group-count'>{list.length}</span>
                                        </div>
                                        <ul className='Links_list' onCopy={this.handleCopy}>
                                            {list.map((item) => {
                                                const name = (item.NAME || '').trim();
                                                const city = (item.CITY || '').trim();
                                                const state = (item.STATE || '').trim();
                                                const location = [city, state].filter(Boolean).join(', ');
                                                const url = String(item.FLIP_BOOK).trim();
                                                return (
                                                    <li className='Links_item' key={item.ARCIMEDES_ROW_ID}>
                                                        <a
                                                            className='Links_anchor'
                                                            href={url}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            data-id={item.ARCIMEDES_ROW_ID}
                                                        >
                                                            <span className='Links_anchor-name'>{name || '(unnamed)'}</span>
                                                            {location && <span className='Links_anchor-sep'> - </span>}
                                                            {location && <span className='Links_anchor-location'>{location}</span>}
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </section>
                    );
                })}
            </div>
        );
    }
}
